import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { DateService } from '@softline/core';
import { GarageOrder } from '../models/garage-order';
import { GarageTask } from '../models/garage-task';
import { Mechanic } from '../models/mechanic';
import { TimeReport } from '../models/time-report';
import { MechanicRepository } from '../repositories/mechanic.repository';
import { TimeReportRepository } from '../repositories/time-report.repsitory';
import { OrderService } from './order.service';
import { GarageOrderRepository } from '../repositories/garage-order.repository';

@Injectable()
export class MechanicService {
  readonly #mechanicRepository = inject(MechanicRepository);
  readonly #orderRepository = inject(GarageOrderRepository);
  readonly #timeReportRepository = inject(TimeReportRepository);
  readonly #dateService = inject(DateService);

  readonly #orderService = inject(OrderService);

  //* PRIVATE API *//
  readonly #mechanicsResource = resource({
    loader: async () => {
      const request = await this.#mechanicRepository.retrieve();
      this.#mechanics.set(request);
      return request;
    },
  });

  readonly #mechanicResource = resource({
    params: () => this.#mechanicId(),
    loader: async ({ params }): Promise<Mechanic | null> => {
      if (!params) return null;
      const request = await this.#mechanicRepository.retrieve();
      return request.find((m) => m.id === params) ?? null;
    },
  });

  readonly #unfinishedOrdersResource = resource({
    params: () => this.#mechanicId(),
    loader: async ({ params }) => {
      const mechanicId = params;
      if (!mechanicId) {
        return undefined;
      }
      const request = await this.#orderRepository.retrieveByMechanicId(params);
      this.#unfinishedOrders.set(request);
      return request;
    },
  });

  readonly #timeReportResource = resource({
    params: () => ({ mechanicId: this.#mechanicId(), date: this.#date() }),
    loader: async ({ params }) => {
      const mechanicId = params.mechanicId;
      const date = params.date;
      if (!mechanicId || !date) {
        return null;
      }
      const request = await this.#timeReportRepository.getTimeReportsByMechanicId(
        mechanicId,
        date
      );
      this.#timeReports.set(request);
      return request;
    },
  });

  readonly #activeTimeReportsResource = resource({
    loader: () => {
      const today = this.#dateService.now().split('T')[0];
      return this.#timeReportRepository.getTimeReports({
        onlyActive: true,
        from: today,
        to: today,
      });
    },
  });

  readonly #mechanics = signal<Mechanic[]>([]);
  readonly #unfinishedOrders = signal<GarageOrder[]>([]);
  readonly #mechanicId = signal<number | null>(null);
  readonly #timeReports = signal<TimeReport[]>([]);
  readonly #date = signal<string | null>(null);

  //* PUBLIC API *//
  readonly mechanicsLoading = computed(() => this.#mechanicsResource.isLoading());
  readonly unfinishedOrdersLoading = computed(() =>
    this.#unfinishedOrdersResource.isLoading()
  );
  readonly timeReportsLoading = computed(() => this.#timeReportResource.isLoading());

  readonly mechanicsError = computed(() => this.#mechanicsResource.error());
  readonly unfinishedOrdersError = computed(() =>
    this.#unfinishedOrdersResource.error()
  );
  readonly timeReportsError = computed(() => this.#timeReportResource.error());

  reloadUnfinishedOrders(): void {
    this.#unfinishedOrdersResource.reload();
  }

  reloadTimeReports(): void {
    this.#timeReportResource.reload();
  }

  readonly mechanics = linkedSignal(() => this.#mechanics());
  readonly mechanic = linkedSignal(() => this.#mechanicResource.value() ?? null);
  readonly unfinishedOrders = computed(() => this.#unfinishedOrders());
  readonly timeReports = computed(() => this.#timeReports());

  readonly activeTimeReportsLoading = computed(() =>
    this.#activeTimeReportsResource.isLoading()
  );
  readonly #activeReports = computed(() =>
    this.#activeTimeReportsResource.status() !== 'error'
      ? (this.#activeTimeReportsResource.value() ?? [])
      : []
  );
  readonly activeMechanicIds = computed(
    () =>
      new Set(
        this.#activeReports()
          .map((report) => report.employee?.id)
          .filter((id): id is number => id != null)
      )
  );
  readonly unproductiveMechanicIds = computed(
    () =>
      new Set(
        this.#activeReports()
          .filter((report) => !report.orderId)
          .map((report) => report.employee?.id)
          .filter((id): id is number => id != null)
      )
  );
  readonly currentMechanicStampedIn = computed(() => {
    const id = this.#mechanicId();
    return id != null && this.activeMechanicIds().has(id);
  });

  isMechanicStampedIn(id: number | undefined): boolean {
    return id != null && this.activeMechanicIds().has(id);
  }

  runningReportForMechanic(id: number | undefined): TimeReport | null {
    if (id == null) return null;
    return this.#activeReports().find((report) => report.employee?.id === id) ?? null;
  }

  mechanicStampKind(id: number | undefined): 'order' | 'unproductive' | null {
    if (id == null || !this.activeMechanicIds().has(id)) return null;
    return this.unproductiveMechanicIds().has(id) ? 'unproductive' : 'order';
  }

  reloadActiveTimeReports(): void {
    this.#activeTimeReportsResource.reload();
  }

  //* LOADER HELPER *//
  loadMechanicById(id: number) {
    this.#mechanicId.set(id);
  }

  loadUnfinishedOrdersByMechanicId(id: number) {
    this.#mechanicId.set(id);
  }

  loadTimeReportsByMechanicId(id: number) {
    this.#mechanicId.set(id);
  }

  async startTimeReportForUnproductiveTaskByMechanicId(
    mechanicId: number,
    task: GarageTask
  ) {
    await this.#timeReportRepository.startTimeReportByMechanicId(mechanicId, task);
    this.#timeReportResource.reload();
    this.#activeTimeReportsResource.reload();
    this.#orderService.garageOrderTimeReportsResource.reload();
  }

  async stopTimeReportByMechanicId(mechanicId: number) {
    await this.#timeReportRepository.stopTimeReportByMechanicId(mechanicId);
    this.#timeReportResource.reload();
    this.#activeTimeReportsResource.reload();
    this.#orderService.garageOrderTimeReportsResource.reload();
  }

  async startTimeReportForOrderByMechanicId(
    mechanicId: number,
    order: GarageOrder,
    task: GarageTask
  ) {
    if (!order.id) return;
    await this.#timeReportRepository.startTimeReportByOrderIdAndMechanicId(
      order.id,
      mechanicId,
      task
    );
    this.#timeReportResource.reload();
    this.#activeTimeReportsResource.reload();
    this.#orderService.garageOrderTimeReportsResource.reload();
  }

  setDate(date: string) {
    this.#date.set(date);
  }

  reloadMechanics(): void {
    this.#mechanicsResource.reload();
  }

  searchMechanics(value: string | null | undefined): void {
    const mechanics = [...this.#mechanics()];
    if (!value) {
      this.#mechanics.set(mechanics);
      return;
    }
    const searchText = value.toLowerCase();
    const result = mechanics.filter((mechanic: Mechanic) => {
      return (
        mechanic.name.toLowerCase().includes(searchText) ||
        mechanic.number.toString().includes(searchText)
      );
    });
    this.mechanics.set(result);
    return;
  }
}
